import { useCallback, useEffect, useState } from "react";
import { api, type AdminUserView } from "../../lib/api";
import { useUser } from "../../lib/auth";

function formatDate(s: string | null): string {
  if (!s) return "never";
  return new Date(s).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Admin → Users sub-page. Lists every user, allows role toggle,
 * deactivate / reactivate, and invite by email.
 */
export function UsersPage() {
  const { user: me } = useUser();
  const [users, setUsers] = useState<AdminUserView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    api.admin
      .listUsers()
      .then(setUsers)
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  async function invite() {
    if (!inviteEmail.trim()) return;
    setBusy("invite");
    setError(null);
    try {
      await api.admin.inviteUser(inviteEmail.trim());
      setInviteEmail("");
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function toggleRole(u: AdminUserView) {
    setBusy(`role:${u.id}`);
    setError(null);
    try {
      await api.admin.patchUser(u.id, {
        role: u.role === "admin" ? "user" : "admin",
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function toggleActive(u: AdminUserView) {
    setBusy(`active:${u.id}`);
    setError(null);
    try {
      await api.admin.patchUser(u.id, { is_active: !u.is_active });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <h1 className="mb-6 text-2xl font-semibold">Users</h1>

      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-slate-700">Invite a user</h2>
        <p className="mt-1 text-xs text-slate-500">
          Auto-provisions a record so the user can sign in immediately.
          @datacamp.com only.
        </p>
        <div className="mt-3 flex gap-2">
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="alice@datacamp.com"
            className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:ring-slate-500"
          />
          <button
            type="button"
            onClick={invite}
            disabled={busy === "invite" || !inviteEmail.trim()}
            className="rounded-md bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {busy === "invite" ? "Inviting…" : "Invite"}
          </button>
        </div>
      </section>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {users === null ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Last seen</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {users.map((u) => {
                const isMe = u.email === me?.email;
                return (
                  <tr key={u.id}>
                    <td className="px-4 py-2">
                      {u.email}
                      {isMe && (
                        <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                          you
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          u.role === "admin"
                            ? "rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
                            : "rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                        }
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {u.is_active ? (
                        <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">
                          active
                        </span>
                      ) : (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                          deactivated
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {formatDate(u.last_seen_at)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => toggleRole(u)}
                          disabled={busy === `role:${u.id}`}
                          className="rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50"
                        >
                          {u.role === "admin" ? "Demote" : "Promote"}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(u)}
                          disabled={busy === `active:${u.id}` || isMe}
                          title={isMe ? "Cannot deactivate yourself" : ""}
                          className={
                            u.is_active
                              ? "rounded-md border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                              : "rounded-md border border-emerald-200 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                          }
                        >
                          {u.is_active ? "Deactivate" : "Reactivate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
